"use client"

import { useState, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Search, ChevronUp, ChevronDown, Eye, EyeOff, Trash2, Plus, X, PenSquare, Download } from "lucide-react"
import { useWorkflow } from "@/context/workflow-context-simple"
import { useTranslation } from '@/hooks/use-translation'
import { useToast } from '@/hooks/use-toast'
import { useWorkflowTracking } from "@/hooks/use-workflow-tracking"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useEntityType } from "@/contexts/entity-type-context"
import {
  isRequestsGroupEntity,
  useRequestsGroupState,
  type RequestsGroupEntityType,
} from "@/contexts/requests-group-state-context"
import type { SavedView } from '@/types/saved-view'
import { SaveViewModal } from './view-selector'

interface ManageViewsModalProps {
  isOpen: boolean
  onClose: () => void
}

interface ViewItemProps {
  view: SavedView
  onView: () => void
  onVisibility: () => void
  onDelete: () => void
  onEdit: () => void
  canEdit: boolean
  canDelete: boolean
  isHidden?: boolean
}

function ViewItem({ view, onView, onVisibility, onDelete, onEdit, canEdit, canDelete, isHidden = false }: ViewItemProps) {
  const { t } = useTranslation()
  
  return (
    <div className="flex items-center justify-between py-2 px-3 hover:bg-gray-50">
      <div className="flex items-center space-x-2 flex-1 min-w-0">
        <span className="text-sm font-medium truncate">{view.title}</span>
        {/* Read-only indicator: this view has the per-view export opt-in
            enabled. Surfaces a small Download glyph next to the title so an
            operator scanning the list can spot which views will show the
            Export button on the grid without opening Edit. Intentionally a
            <span>, not a button — there's no click affordance, and the
            tooltip carries the explanation. Hidden when the flag is false
            so the row stays uncluttered for the common case. */}
        {view.allowDataExport && (
          <span
            className="inline-flex items-center text-emerald-600 flex-shrink-0"
            title={t('viewAllowDataExportEnabled' as any) || 'Data export is enabled for this view'}
            aria-label={t('viewAllowDataExportEnabled' as any) || 'Data export is enabled for this view'}
          >
            <Download size={13} />
          </span>
        )}
      </div>
      <div className="flex items-center space-x-0.5">
        <Button
          variant="ghost"
          size="sm"
          onClick={onVisibility}
          className="h-8 w-8 p-0 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-all"
          title={view.isVisible ? (t('hideView') || "Hide view") : (t('showView') || "Show view")}
        >
          {view.isVisible ? <Eye size={15} /> : <EyeOff size={15} />}
        </Button>
        {canEdit && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            className="h-8 w-8 p-0 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-all"
            title={t('editView') || "Edit view"}
          >
            <PenSquare size={15} />
          </Button>
        )}
        {canDelete && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="h-8 w-8 p-0 rounded-md text-gray-500 hover:text-red-500 hover:bg-red-50 transition-all"
            title={t('deleteView') || "Delete view"}
          >
            <Trash2 size={15} />
          </Button>
        )}
      </div>
    </div>
  )
}

export function ManageViewsModal({ isOpen, onClose }: ManageViewsModalProps) {
  const { viewsResponse, currentView, setCurrentView, deleteView, updateViewMetadata, refreshViews, saveViewStateSilently, saveViewAs, configuration } = useWorkflow()
  const { updateViewVisibility, saveViewSettings } = useWorkflowApi()
  const { t } = useTranslation()
  const { toast } = useToast()
  const { trackViewAction } = useWorkflowTracking()
  // For visibility toggles on a Requests-group view, the BE fans out the
  // change across all sibling UserViewStates — bump the shared refresh
  // signal so any already-mounted sibling providers re-fetch and update
  // their own dropdowns / saved-view lists without a tab remount.
  const { entityType: contextEntityType } = useEntityType()
  const requestsGroupState = useRequestsGroupState()

  const [searchTerm, setSearchTerm] = useState("")
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [viewToDelete, setViewToDelete] = useState<SavedView | null>(null)
  const [viewToEdit, setViewToEdit] = useState<SavedView | null>(null)
  
  // State for save view modal
  const [isSaveViewModalOpen, setIsSaveViewModalOpen] = useState(false)
  const [viewTitle, setViewTitle] = useState("")
  const [isPublic, setIsPublic] = useState(false)
  const [isClientFacingEdit, setIsClientFacingEdit] = useState(false)
  // Seeded from the edited view on open so the SaveViewModal renders the
  // AllowDataExport checkbox already reflecting the persisted value. Reset
  // on modal close so a subsequent "Edit" doesn't carry over stale state.
  const [allowDataExportEdit, setAllowDataExportEdit] = useState(false)

  const searchInputRef = useRef<HTMLInputElement>(null)

  // State for collapsible sections
  const [collapsedSections, setCollapsedSections] = useState<{
    myViews: boolean
    sharedWithMe: boolean
    allCompany: boolean
  }>({
    myViews: false,
    sharedWithMe: false,
    allCompany: false
  })

  // State for showing more views - track how many items to show for each section
  const [visibleCounts, setVisibleCounts] = useState<{
    myViews: number
    sharedWithMe: number
    allCompany: number
  }>({
    myViews: 5,
    sharedWithMe: 5,
    allCompany: 5
  })

  // Get the different view types from the API response
  const defaultView = viewsResponse?.defaultView || null
  const userViews = viewsResponse?.userViews || []
  const sharedViews = viewsResponse?.sharedViews || []
  const publicViews = viewsResponse?.publicViews || []

  // Get authorized actions for permission checking
  const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode)
  const hasModifyPublicViewsPermission = authorizedActions.includes("ModifyPublicViews")
  const hasManageViewsPermission = authorizedActions.includes("ManageViews")
  const hasManageClientViewsPermission = authorizedActions.includes("ManageClientViews")
  // Drives whether the SaveViewModal renders the Allow Data Export checkbox.
  // Same flag the grid header uses to gate the Export button itself — keeping
  // the editor and the consumer on the same permission avoids the confused
  // state where someone sets a flag they can't benefit from.
  const hasExportDataPermission = authorizedActions.includes("ExportData")

  // Filter views based on search term
  const filterViewsBySearch = (views: SavedView[]) => {
    if (!searchTerm.trim()) return views
    return views.filter((view) => 
      view.title.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }

  const filteredUserViews = filterViewsBySearch(userViews)
  const filteredSharedViews = filterViewsBySearch(sharedViews)
  const filteredPublicViews = filterViewsBySearch(publicViews)

  // Get limited views for display
  const getLimitedViews = (views: SavedView[], section: 'myViews' | 'sharedWithMe' | 'allCompany') => {
    // Always show all views when searching
    if (searchTerm.trim()) {
      return views
    }
    // Show up to the visible count for this section
    const result = views.slice(0, visibleCounts[section])
    return result
  }

  const limitedUserViews = getLimitedViews(filteredUserViews, 'myViews')
  const limitedSharedViews = getLimitedViews(filteredSharedViews, 'sharedWithMe')
  const limitedPublicViews = getLimitedViews(filteredPublicViews, 'allCompany')

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  // Maintain focus on search input when modal is open
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus()
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("")
      setViewToDelete(null)
      setViewToEdit(null)
      setViewTitle("")
      setIsPublic(false)
      setIsClientFacingEdit(false)
      setAllowDataExportEdit(false)
      setVisibleCounts({
        myViews: 5,
        sharedWithMe: 5,
        allCompany: 5
      })
    }
  }, [isOpen])

  // Toggle section collapse
  const toggleSection = (section: 'myViews' | 'sharedWithMe' | 'allCompany') => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  // Handle view selection
  const handleViewSelect = async (view: SavedView) => {
    if (currentView?.viewSettingsId !== view.viewSettingsId) {
      trackViewAction('load', view.title)
      await setCurrentView(view)
    }
  }

  // Handle view visibility
  const handleViewVisibility = async (view: SavedView) => {
    try {
      // If view is currently visible, we want to hide it (remove = true)
      // If view is currently hidden, we want to show it (remove = false)
      const remove = view.isVisible === true
      
      await updateViewVisibility(view.viewSettingsId, remove)

      // Refresh views to get updated visibility state - use the fresh response
      const freshViewsResponse = await refreshViews()

      // Save view state to persist visibleViewIds using the fresh response from GET views
      await saveViewStateSilently(undefined, freshViewsResponse)

      // The BE's UpdateUserVisibleViews flow now fans the toggle out to
      // every sibling UserViewState in the group (so hiding "Deloitte" on
      // VMS Requests also hides it on VMS Emails + Client Requests +
      // ATS). Tell already-mounted sibling providers to re-read their
      // /views + /configurations so the dropdown reflects the new
      // visibility immediately — without this, the user sees the change
      // on the active tab but other tabs keep showing the toggled view
      // in their dropdown until they remount. Origin=self → skip-self
      // because the active tab's saveViewStateSilently above already
      // pulled fresh state inline.
      if (isRequestsGroupEntity(contextEntityType)) {
        requestsGroupState?.bumpRefreshSignal(contextEntityType as RequestsGroupEntityType)
      }

      trackViewAction('edit', view.title)
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('error') || "Error",
        description: error instanceof Error ? error.message : "Failed to update view visibility",
      })
    }
  }

  // Handle delete view
  const handleDeleteView = (view: SavedView) => {
    setViewToDelete(view)
    setIsDeleteDialogOpen(true)
    trackViewAction('delete', view.title)
  }

  // Handle edit view
  const handleEditView = async (view: SavedView) => {
    try {
      setViewToEdit(view)
      setViewTitle(view.title)
      setIsPublic(view.isPublic || false)
      setIsClientFacingEdit(view.isClientFacing || false)
      // Seed the export toggle from the persisted view so the modal opens with
      // its current state. New views (no view passed in this path) fall back
      // to false via the state's initial value.
      setAllowDataExportEdit(view.allowDataExport || false)
      setIsSaveViewModalOpen(true)

      trackViewAction('edit', view.title)
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('error') || "Error",
        description: error instanceof Error ? error.message : "Failed to load view data",
      })
    }
  }

  // Handle save view (create or edit)
  const handleSaveView = async (
    title: string,
    isPublic: boolean,
    saveFilters: boolean,
    isClientFacing: boolean,
    authorizedClients?: { entityId: number; name: string }[],
    _alsoSaveEntityTypes?: string[],
    allowDataExport?: boolean,
  ) => {
    const visibleInApplications = isClientFacing ? ['Client'] : []
    try {
      if (viewToEdit) {
        const saveRequest: any = {
          id: viewToEdit.viewSettingsId,
          title: title,
          isDefault: false,
          isPublic: isPublic,
          // The BE update branch unconditionally writes AllowDataExport, so a
          // dropped/omitted field would deserialize to the C# default (false)
          // and clobber the persisted value. When the modal hides the toggle
          // (operator lacks ExportData) the callback omits the value — fall
          // back to whatever the view already has so we preserve the state
          // we read on open.
          allowDataExport: allowDataExport ?? (viewToEdit.allowDataExport ?? false),
          sharedUserIds: [],
          stageConfigurations: [],
          stageFilters: [],
          globalFilters: [],
          columnWidths: "",
          itemsPerPage: 0,
          isTitleAndVisibilityUpdate: true,
          isUserViewStateUpdate: false,
          updateFilters: false,
          visibleInApplications,
          authorizedClientList: authorizedClients,
        }

        await saveViewSettings(saveRequest)
        
        trackViewAction('edit', title)
        toast({
          variant: "success",
          title: t('success') || "Success",
          description: "View updated successfully",
        })
      } else {
        const newView = await saveViewAs(title, isPublic, visibleInApplications)
        
        trackViewAction('create', title)
        toast({
          variant: "success",
          title: t('success') || "Success",
          description: "View created successfully",
        })
        
        await setCurrentView(newView)
      }
      
      await refreshViews()
      
      setIsSaveViewModalOpen(false)
      setViewToEdit(null)
      setViewTitle("")
      setIsPublic(false)
      setIsClientFacingEdit(false)
    } catch (error) {      
      toast({
        variant: "destructive",
        title: t('error') || "Error",
        description: error instanceof Error ? error.message : "Failed to save view",
      })
    }
  }



  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!viewToDelete) return

    try {
      await deleteView(viewToDelete.viewSettingsId)
      
      // If the deleted view was the current view, set a new current view
      if (currentView?.viewSettingsId === viewToDelete.viewSettingsId) {
        // Try to set default view first, otherwise pick the first available view
        const defaultView = viewsResponse?.defaultView
        const userViews = viewsResponse?.userViews || []
        const sharedViews = viewsResponse?.sharedViews || []
        const publicViews = viewsResponse?.publicViews || []
        
        const newCurrentView = defaultView || userViews[0] || sharedViews[0] || publicViews[0]
        
        if (newCurrentView) {
          await setCurrentView(newCurrentView)
        }
      }
      
      toast({
        variant: "success",
        title: t('success') || "Success",
        description: t('deleteView') || "View deleted successfully",
      })
      setIsDeleteDialogOpen(false)
      setViewToDelete(null)
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('error') || "Error",
        description: error instanceof Error ? error.message : "Failed to delete view",
      })
    }
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[600px] h-[700px] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('manageViews') || "Manage views"}</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                ref={searchInputRef}
                placeholder={t('searchViews') || "Search views"}
                className="pl-9 pr-8"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Views List */}
            <div className="flex-1 overflow-y-auto space-y-4">
              {/* My views section */}
              <div>
                <div 
                  onClick={() => toggleSection('myViews')}
                  className="flex items-center justify-between px-2 py-1.5 text-xs font-medium text-gray-700 cursor-pointer hover:bg-gray-50 rounded"
                >
                  <span>{t('myViews') || "My views"}</span>
                  {collapsedSections.myViews ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                </div>
                <div className={`overflow-hidden transition-all duration-200 ease-in-out ${collapsedSections.myViews ? 'max-h-0' : 'max-h-none'}`}>
                  {limitedUserViews.map((view, index) => (
                    <div key={view.viewSettingsId}>
                      <ViewItem
                        view={view}
                        onView={() => handleViewSelect(view)}
                        onVisibility={() => handleViewVisibility(view)}
                        onDelete={() => handleDeleteView(view)}
                        onEdit={() => handleEditView(view)}
                        canEdit={true}
                        canDelete={true}
                      />
                      {index < limitedUserViews.length - 1 && (
                        <div className="border-b border-gray-200 mx-3" />
                      )}
                    </div>
                  ))}
                  {filteredUserViews.length > visibleCounts.myViews && !searchTerm.trim() && (
                    <div className="px-3 py-2">
                      <button
                        onClick={() => {
                          setVisibleCounts(prev => {
                            const newCount = Math.min(prev.myViews + 5, filteredUserViews.length)
                            return { ...prev, myViews: newCount }
                          })
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700 w-full text-left"
                      >
                        {t('showMore') || "Show more"} ({filteredUserViews.length - visibleCounts.myViews})
                      </button>
                    </div>
                  )}
                  {filteredUserViews.length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      {t('noMyViews') || "No views found"}
                    </div>
                  )}
                </div>
              </div>



              {/* All company section */}
              <div>
                <div 
                  onClick={() => toggleSection('allCompany')}
                  className="flex items-center justify-between px-2 py-1.5 text-xs font-medium text-gray-700 cursor-pointer hover:bg-gray-50 rounded"
                >
                  <span>{t('allCompany') || "All company"}</span>
                  {collapsedSections.allCompany ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                </div>
                <div className={`overflow-hidden transition-all duration-200 ease-in-out ${collapsedSections.allCompany ? 'max-h-0' : 'max-h-none'}`}>
                  {limitedPublicViews.map((view, index) => (
                    <div key={view.viewSettingsId}>
                      <ViewItem
                        view={view}
                        onView={() => handleViewSelect(view)}
                        onVisibility={() => handleViewVisibility(view)}
                        onDelete={() => handleDeleteView(view)}
                        onEdit={() => handleEditView(view)}
                        canEdit={hasModifyPublicViewsPermission}
                        canDelete={false}
                      />
                      {index < limitedPublicViews.length - 1 && (
                        <div className="border-b border-gray-200 mx-3" />
                      )}
                    </div>
                  ))}
                  {filteredPublicViews.length > visibleCounts.allCompany && !searchTerm.trim() && (
                    <div className="px-3 py-2">
                      <button
                        onClick={() => {
                          setVisibleCounts(prev => {
                            const newCount = Math.min(prev.allCompany + 5, filteredPublicViews.length)
                            return { ...prev, allCompany: newCount }
                          })
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700 w-full text-left"
                      >
                        {t('showMore') || "Show more"} ({filteredPublicViews.length - visibleCounts.allCompany})
                      </button>
                    </div>
                  )}
                  {filteredPublicViews.length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      {t('noPublicViews') || "No public views found"}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            {hasManageViewsPermission && (
              <div className="flex items-center justify-end pt-4 border-t">
                <Button 
                  onClick={() => {
                    setViewTitle("")
                    setIsPublic(false)
                    setViewToEdit(null)
                    setIsSaveViewModalOpen(true)
                    trackViewAction('create', 'New View')
                  }} 
                  className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 bg-transparent hover:bg-blue-50 border border-blue-200 hover:border-blue-300 px-4 py-2 rounded-md"
                >
                  <Plus size={16} />
                  <span>Create view</span>
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Save View Modal for Create/Edit View */}
      <SaveViewModal
        isOpen={isSaveViewModalOpen}
        onClose={() => {
          setIsSaveViewModalOpen(false)
          setViewTitle("")
          setViewToEdit(null)
          setIsPublic(false)
          setIsClientFacingEdit(false)
          setAllowDataExportEdit(false)
        }}
        onSave={handleSaveView}
        initialTitle={viewTitle}
        initialIsPublic={isPublic}
        initialIsClientFacing={isClientFacingEdit}
        initialAllowDataExport={allowDataExportEdit}
        initialAuthorizedClients={(viewToEdit?.authorizedClientList || []).map(c => ({ entityId: c.entityId, name: c.name }))}
        canManageClientViews={hasManageClientViewsPermission}
        canExportData={hasExportDataPermission}
        isEdit={!!viewToEdit}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteViewConfirmation') || "Are you sure you want to delete this view?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteViewWarning') || "This view will be deleted for everyone it has been shared with. You cannot undo this action."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel variant="elliptical-outline">{t('cancel') || "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} variant="elliptical">
              {t('delete') || "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
} 